/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Fragment, MouseEvent as ReactMouseEvent, useState } from 'react';
import { useStyles } from '~src/hooks/useStyles';
import { DbAnswer } from '~src/types/db';
import { styleFn } from './style';
import { Dropdown, Menu, message, Modal } from 'antd'; // Додано Modal
import { ExclamationCircleOutlined } from '@ant-design/icons'; // Додано іконку для модалки
import { formatDate } from '~src/utils/formatDate';
import IconButton from '~components/_common/IconButton/IconButton';
import { useIntl } from 'react-intl';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { queryCache, useMutation } from 'react-query';
import { changeAnswerState, getAnswer } from '~src/services/api/questions';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';

const { confirm } = Modal; // Використовуємо confirm з Modal

export type AnswerItemProps = {
  answer: DbAnswer;
  canDelete: boolean;
  withAnswerAuthor?: boolean;
  isOnTender?: boolean;
  projectId: number;
  tenderId: number;
};

const AnswerItem: React.FC<AnswerItemProps> = ({
  answer,
  canDelete,
  withAnswerAuthor,
  isOnTender,
  projectId,
  tenderId,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [rightClickAnswer, setRightClickAnswer] = useState<{
    answer?: DbAnswer;
  }>({});

  const [fetchedDeletedContent, setFetchedDeletedContent] = useState<
    string | null
  >(null);

  const [doManageAnswerState] = useMutation(changeAnswerState, {
    onSuccess: () => {
      queryCache.invalidateQueries(QueryKeys.question);
      message.success(
        intl.formatMessage({ id: 'question-asnwer-state-updated' })
      );
    },
  });

  const isRemoved = answer.state === 9; // Answer REMOVED
  const isRejected = answer.state === 5; // Answer REJECTED
  const isActive = answer.state === 3; // Answer AVAILABLE

  const rightClickSelect = (e: ReactMouseEvent<any>, answer: DbAnswer) => {
    setRightClickAnswer({ answer });
  };

  const handleContextMenuEvent = (e: ReactMouseEvent<any>) => {
    if (isOnTender) {
      e.preventDefault();
      return;
    }

    const customEvent = new MouseEvent('contextmenu', {
      bubbles: false,
      cancelable: true,
      view: window,
      button: 2,
      buttons: 2,
      clientX: e.clientX,
      clientY: e.clientY,
    });
    e.currentTarget.dispatchEvent(customEvent);
  };

  const handleLoadDeletedContent = async (pickedAnswer: DbAnswer) => {
    try {
      const fullAnswer = await getAnswer({
        projectId,
        tenderId,
        questionId: pickedAnswer.question_id,
        answerId: pickedAnswer.id,
      });
      if (fullAnswer && fullAnswer.answer) {
        setFetchedDeletedContent(fullAnswer.answer);
        setIsContentVisible(true);
        return fullAnswer.answer; // Повертаємо текст для використання в restore
      }
      return null;
    } catch (e) {
      message.error(intl.formatMessage({ id: 'failed' }));
      return null;
    }
  };

  // restore logic with confirmation modal
  const handleRestoreClick = async (pickedAnswer: DbAnswer) => {
    let contentToShow = fetchedDeletedContent || pickedAnswer.answer;

    if (!contentToShow && isRemoved) {
      contentToShow = await handleLoadDeletedContent(pickedAnswer);
    }

    confirm({
      title: intl.formatMessage({
        id: 'qa-answer-restore-confirm-title',
      }),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div style={{ marginTop: '10px' }}>
          <p
            style={{
              fontStyle: 'italic',
              color: '#595959',
              whiteSpace: 'pre-line',
            }}>
            {contentToShow ||
              intl.formatMessage({
                id: 'no-content',
                defaultMessage: 'No content available.',
              })}
          </p>
        </div>
      ),
      okText: intl.formatMessage({ id: 'yes' }),
      cancelText: intl.formatMessage({ id: 'no' }),
      onOk: async () => {
        await doManageAnswerState({
          projectId,
          tenderId,
          questionId: pickedAnswer.question_id,
          answerId: pickedAnswer.id,
          action: 'restore',
        });
      },
    });
  };

  const handleRightOverlayItemClick = async (item: DataContextMenuItemType) => {
    const picked = rightClickAnswer.answer ?? answer;
    const questionId = picked?.question_id;
    const answerId = picked?.id;

    if (item.modal === 'show_deleted_content') {
      await handleLoadDeletedContent(picked);
      return;
    }

    if (item.modal === 'restore') {
      await handleRestoreClick(picked);
      return;
    }

    switch (item.modal) {
      case 'remove':
      case 'reject':
        await doManageAnswerState({
          projectId,
          tenderId,
          questionId,
          answerId,
          action: item.modal,
        });
        break;
      default:
        break;
    }
  };

  // context menu logic
  const menuItems = (isOnTender ? [] : dataContextMenu.answerMenu).filter(
    (item) => {
      if (item.modal === 'show_deleted_content') {
        return isRemoved && fetchedDeletedContent === null;
      }
      if (isRejected || isRemoved) return item.modal === 'restore';
      if (isActive) return item.modal !== 'restore';
      return true;
    }
  );

  const answerDetailsMenu = (
    <Menu onClick={() => setIsDropdownVisible(false)}>
      {menuItems.map((item) => (
        <Menu.Item
          key={item.modal}
          onClick={async () => {
            if (item.modal === 'show_deleted_content') {
              await handleLoadDeletedContent(answer);
            } else if (item.modal === 'restore') {
              await handleRestoreClick(answer);
            } else {
              await doManageAnswerState({
                projectId,
                tenderId,
                questionId: answer.question_id,
                answerId: answer.id,
                action: item.modal as any,
              });
            }
          }}>
          <IconButton icon={[item.iconStyle as any, item.icon]} />{' '}
          {intl.formatMessage({ id: item.name })}
        </Menu.Item>
      ))}
    </Menu>
  );

  if (answer.answer === null && !isRemoved) {
    return null;
  }

  const statusTags = (
    <Fragment>
      {isRemoved && (
        <span
          style={{
            marginLeft: '8px',
            color: '#8c8c8c',
            fontSize: '12px',
            fontWeight: 'normal',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}>
          ({intl.formatMessage({ id: 'qa-answer-state-removed' })})
          {!isOnTender && fetchedDeletedContent && (
            <span
              onClick={() => setIsContentVisible(!isContentVisible)}
              style={{
                cursor: 'pointer',
                color: '#1890ff',
                textDecoration: 'underline',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
              }}>
              {intl.formatMessage({
                id: isContentVisible
                  ? 'qa-answer-click-to-hide'
                  : 'qa-answer-click-to-open',
              })}
              <IconButton
                icon={
                  isContentVisible
                    ? ['far', 'chevron-up']
                    : ['far', 'chevron-down']
                }
                containerStyle={{
                  minHeight: 'auto',
                  minWidth: 'auto',
                  padding: 0,
                }}
                customStyle={{ fontSize: '10px' }}
              />
            </span>
          )}
        </span>
      )}
      {isRejected && (
        <span
          style={{
            marginLeft: '8px',
            color: '#cf1322',
            fontSize: '12px',
            fontWeight: 'normal',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}>
          ({intl.formatMessage({ id: 'qa-answer-state-rejected' })})
          <span
            onClick={() => setIsContentVisible(!isContentVisible)}
            style={{
              cursor: 'pointer',
              color: '#1890ff',
              textDecoration: 'underline',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
            }}>
            {intl.formatMessage({
              id: isContentVisible
                ? 'qa-answer-click-to-hide'
                : 'qa-answer-click-to-open',
            })}
            <IconButton
              icon={
                isContentVisible
                  ? ['far', 'chevron-up']
                  : ['far', 'chevron-down']
              }
              containerStyle={{
                minHeight: 'auto',
                minWidth: 'auto',
                padding: 0,
              }}
              customStyle={{ fontSize: '10px' }}
            />
          </span>
        </span>
      )}
    </Fragment>
  );

  const shouldShowContent =
    isActive ||
    (isRejected && isContentVisible) ||
    (isRemoved && !isOnTender && isContentVisible);

  return (
    <BaseRightClickOverlay
      items={menuItems}
      onItemClick={handleRightOverlayItemClick}
      onOverlayClose={() => setRightClickAnswer({})}>
      <div
        style={{
          margin: '10px 0 0 0',
          padding: '10px',
          backgroundColor: isRemoved
            ? '#f0f0f0'
            : isRejected
            ? '#fff1f0'
            : '#f5f5f5',
          position: 'relative',
          borderLeft: isRejected
            ? '4px solid #ff4d4f'
            : isRemoved
            ? '4px solid #d9d9d9'
            : 'none',
          opacity: isRemoved ? 0.7 : 1,
        }}
        onContextMenu={(e) => {
          if (isOnTender) e.preventDefault();
          rightClickSelect(e, answer);
        }}>
        {withAnswerAuthor ? (
          <p
            style={{
              fontWeight: 'bold',
              color: isRemoved ? '#8c8c8c' : 'inherit',
              marginBottom:
                isRemoved || (isRejected && !isContentVisible) ? 0 : '8px',
            }}>
            {answer.created_by?.full_name} -{' '}
            {formatDate(answer.created_at, 'long')}
            {statusTags}
          </p>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              fontWeight: 'bold',
              color: isRemoved ? '#8c8c8c' : 'inherit',
              marginBottom:
                isRemoved || (isRejected && !isContentVisible) ? 0 : '8px',
            }}>
            {intl.formatMessage({ id: 'qa-answer-author-not-found' })} -{' '}
            {formatDate(answer.created_at, 'long')}
            {statusTags}
          </div>
        )}

        {shouldShowContent && (fetchedDeletedContent || answer.answer) && (
          <p
            style={{
              fontStyle: 'italic',
              whiteSpace: 'pre-line',
              wordBreak: 'break-word',
              margin: 0,
              color: isRejected ? '#595959' : 'inherit',
            }}>
            {fetchedDeletedContent !== null
              ? fetchedDeletedContent
              : answer.answer}
          </p>
        )}

        {/* Context Menu Dots */}
        <div css={styles.answerActionsButton}>
          {canDelete && (
            <Dropdown
              visible={isDropdownVisible}
              onVisibleChange={setIsDropdownVisible}
              trigger={['click']}
              overlay={answerDetailsMenu}>
              <IconButton
                click={(e) => {
                  e.preventDefault();
                  handleContextMenuEvent(e);
                  setIsDropdownVisible((prevIsVisible) => !prevIsVisible);
                }}
                icon={['far', 'ellipsis']}
              />
            </Dropdown>
          )}
        </div>
      </div>
    </BaseRightClickOverlay>
  );
};

export default AnswerItem;
